import { Ref, ref, watch, computed } from '@vue/composition-api';
import { encode } from 'js-base64';
import smh from '@/renderer/scripts/smh';
import user from '@/renderer/scripts/utils/user';
import storage from '@/renderer/scripts/utils/storage';
import store from '@/renderer/store';
import tips from '@/renderer/scripts/utils/tips';

import { verifyAccountLogin } from '@/renderer/scripts/utils/user/userLogin';

type LoginConfig = {
  protocol: 'openLdap' | 'windowsAd';
  loginFlagShow: string;
};

type Handler = {
  corpId: Ref<string>;
  corpIdList: Ref<string[]>;
  loading: Ref<boolean>;
  step: Ref<number>;
  loginConfigList: Ref<LoginConfig[]>;
  ldapType: Ref<string>;
  loginFlag: Ref<string>;

  methods: {
    initList: () => void;
    addCustomIdList: () => void;
    getLoginConfig: (domain?: string) => Promise<void>;
    confirmCropId: () => Promise<boolean>;
    confirmDomain: (domain: string) => Promise<boolean>;
    onSubmit: (accountName: string, accountPassword: string) => Promise<void>;
  };
};
export default function useLDAPLogin(): Handler {
  // 企业id下拉列表最多存5项
  const listLimit = 5;
  const corpId = ref('');
  const corpIdList: Ref<string[]> = ref([]);
  const loading = ref(false);
  const remoteCropList = computed(() => store.getters.corpList);

  const step = ref(1);

  const loginConfigList: Ref<LoginConfig[]> = ref([]);
  const ldapType = ref('');
  const loginFlag = ref('');

  const methods = {
    // 初始化查询缓存的企业id
    initList() {
      if (remoteCropList.value.length > 0) {
        corpId.value = remoteCropList.value[0].corpId;
      } else {
        const storeCorpIdList = storage.get<string[]>(storage.StoreKey.LDAP_CORPID_LIST) || [];
        corpIdList.value = storeCorpIdList.filter((item: string) => typeof item === 'string');
        // 默认选中最新一条企业id
        if (corpIdList.value.length > 0) {
          [corpId.value] = corpIdList.value;
        }
      }
    },
    // 确认时缓存企业id
    addCustomIdList() {
      const list = corpIdList.value;
      const storeKey = storage.StoreKey.LDAP_CORPID_LIST;
      if (!list.includes(corpId.value)) {
        list.unshift(corpId.value);
      }
      if (list.length > listLimit) {
        list.pop();
      }
      storage.set(storeKey, list);
    },
    // 获取登录配置
    async getLoginConfig(domain?: string) {
      try {
        loading.value = true;
        const params = domain ? { domain } : { orgDomain: encodeURIComponent(corpId.value) };
        const { data } = await smh.api.getLdapLoginConfig(params);
        if (data.list && data.list.length > 0) {
          loginConfigList.value = data.list;
          const defaultConfig = data.list[0];
          ldapType.value = defaultConfig.protocol;
          loginFlag.value = defaultConfig.loginFlagShow;
        }
      } catch (e: any) {
        loginConfigList.value = [];
        ldapType.value = '';
        loginFlag.value = '';
        tips.error(e);
      }
      loading.value = false;
    },
    // 输入企业id
    async confirmCropId() {
      await methods.getLoginConfig();
      if (!ldapType.value) return false;
      // 记录企业id
      user.setCorpid(corpId.value);
      methods.addCustomIdList();
      step.value = 2;
      return true;
    },
    // 自定义域名自动填充域名
    async confirmDomain(domain: string) {
      await methods.getLoginConfig(domain);
      if (!ldapType.value) return false;
      step.value = 2;
      return true;
    },
    // 完成登录
    async onSubmit(accountName: string, accountPassword: string) {
      try {
        await verifyAccountLogin({
          type: 'account-password',
          orgDomain: corpId.value,
          ldapType: ldapType.value,
          accountName,
          accountPassword: encode(accountPassword),
        });
      } catch (e: any) {
        tips.error(e);
      }
    },
  };

  // 企业id不支持空格
  watch(corpId, val => {
    corpId.value = `${val}`.replace(/\s/g, '').replace(/\./g, '');
  });

  // 登录方式变化loginFlag需变化
  watch(ldapType, val => {
    const loginConfigItem = loginConfigList.value.find(item => item.protocol === val);
    loginFlag.value = (loginConfigItem as LoginConfig).loginFlagShow;
  });

  return {
    corpId,
    corpIdList,
    loading,
    step,
    loginConfigList,
    ldapType,
    loginFlag,
    methods,
  };
}
